import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { gql } from 'apollo-boost'
import { useApolloClient } from '@apollo/react-hooks'
import { GET_MESSAGE_APPEARANCE_V2_QUERY } from '../message-appearance-v2/hooks/useGetMessageAppearance'
import {
  GetMessageAppearanceV2,
  GetMessageAppearanceV2Variables
} from '../message-appearance-v2/hooks/__generated__/GetMessageAppearanceV2'

const BOARD_PREVIEW_META = gql`
  query BoardPreviewMetaQuery($boardId: String!) {
    board(id: $boardId) {
      id
      title
      boardStyle
    }
  }
`

type BoardPreviewMetaQuery = {
  board: { id: string; title: string | null; boardStyle: string } | null
}

type BoardPreviewMetaVariables = { boardId: string }

export const MESSAGE_APPEARANCE_PREVIEW_BATCH_SIZE = 4

export type MessageAppearancePreviewEntry =
  | { status: 'error'; message: string }
  | {
      status: 'ok'
      id: string
      boardId: string
      boardTitle: string | null
      boardStyle: string
      created: number
      messageText: string | null
      characters: number[][]
    }

export function orderedAppearanceIdsFromRows(rows: { messageAppearanceId: string | null }[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const row of rows) {
    if (!row.messageAppearanceId || seen.has(row.messageAppearanceId)) continue
    seen.add(row.messageAppearanceId)
    out.push(row.messageAppearanceId)
  }
  return out
}

export function useBatchedMessageAppearancePreviews(subscriptionId: string, orderedAppearanceIds: string[]) {
  const client = useApolloClient()
  const [cache, setCache] = useState<Record<string, MessageAppearancePreviewEntry | undefined>>({})

  useLayoutEffect(() => {
    setCache({})
  }, [subscriptionId])

  const orderedIdsKey = useMemo(() => orderedAppearanceIds.join(','), [orderedAppearanceIds])

  const pendingCount = useMemo(
    () => orderedAppearanceIds.filter(id => cache[id] === undefined).length,
    [orderedAppearanceIds, cache]
  )

  useEffect(() => {
    const batch = orderedAppearanceIds
      .filter(id => cache[id] === undefined)
      .slice(0, MESSAGE_APPEARANCE_PREVIEW_BATCH_SIZE)

    if (batch.length === 0) return

    let alive = true

    ;(async () => {
      const updates: Record<string, MessageAppearancePreviewEntry> = {}

      await Promise.all(
        batch.map(async appearanceId => {
          try {
            const maRes = await client.query<GetMessageAppearanceV2, GetMessageAppearanceV2Variables>({
              query: GET_MESSAGE_APPEARANCE_V2_QUERY,
              variables: { id: appearanceId },
              fetchPolicy: 'cache-first'
            })

            const ma = maRes.data?.messageAppearance
            if (!ma) {
              updates[appearanceId] = { status: 'error', message: 'No data' }
              return
            }
            if (ma.__typename === 'MessageAppearanceErrorV2') {
              updates[appearanceId] = { status: 'error', message: ma.error || 'Message appearance error' }
              return
            }
            if (ma.subscriptionId && ma.subscriptionId !== subscriptionId) {
              updates[appearanceId] = {
                status: 'error',
                message: 'Appearance belongs to a different subscription'
              }
              return
            }

            const boardRes = await client.query<BoardPreviewMetaQuery, BoardPreviewMetaVariables>({
              query: BOARD_PREVIEW_META,
              variables: { boardId: ma.boardId },
              fetchPolicy: 'cache-first'
            })

            const board = boardRes.data?.board
            updates[appearanceId] = {
              status: 'ok',
              id: ma.id,
              boardId: ma.boardId,
              boardTitle: board?.title ?? null,
              boardStyle: board?.boardStyle ?? 'black',
              created: ma.created,
              messageText: ma.message.text,
              characters: ma.message.characters
            }
          } catch (e) {
            updates[appearanceId] = {
              status: 'error',
              message: e instanceof Error ? e.message : 'Failed to load'
            }
          }
        })
      )

      if (!alive) return
      setCache(c => ({ ...c, ...updates }))
    })()

    return () => {
      alive = false
    }
  }, [client, subscriptionId, orderedIdsKey, orderedAppearanceIds, cache])

  return { cache, pendingCount }
}
